import {
  ArtistAbout,
  ArtistAboutListParams,
  ArtistAboutStatus,
  getArtistAboutList,
} from '@/api/artistAbout';
import SearchForm, { SearchFormItem } from '@/components/SearchForm';
import TabsTable, { TabsTableRef } from '@/components/TabsTable';
import { FORMAT_TIME } from '@/types/common';
import { ensureHttpPrefix } from '@/utils/http';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Space,
  TableColumnProps,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const ArtistAboutPage = () => {
  const [form] = Form.useForm<{
    id: string;
    submitTime: string;
    auditUserId: string;
    suppId: string;
    userName: string;
    email: string;
  }>();
  const tabsTableRef = useRef<TabsTableRef>(null);
  const navigate = useNavigate();

  const SearchFormItems: SearchFormItem[] = [
    {
      name: 'id',
      label: '信息 ID',
      component: <Input size="small" />,
    },
    {
      name: 'submitTime',
      label: '提交日期',
      component: (
        <DatePicker size="small" style={{ width: '100%' }} placeholder="" />
      ),
    },
    {
      name: 'email',
      label: '用户 Email',
      component: <Input size="small" />,
    },
    {
      name: 'suppId',
      label: 'SUPP ID',
      component: <Input size="small" />,
    },
    {
      name: 'userName',
      label: '用户名称',
      component: <Input size="small" />,
    },
    {
      name: 'auditUserId',
      label: '操作人',
      component: <Input size="small" />,
    },
  ];

  const onFinish = () => {
    tabsTableRef.current?.refresh();
  };

  const tabItems = [
    {
      key: 'all',
      label: '汇总',
    },
    {
      key: ArtistAboutStatus.APPROVED.toString(),
      label: '发布',
    },
    {
      key: ArtistAboutStatus.REJECTED.toString(),
      label: '驳回',
    },
    {
      key: ArtistAboutStatus.PENDING.toString(),
      label: '待编辑',
    },
  ];

  const handleEditClick = (id: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set('id', id);
    navigate(`/artist-about/editor?${searchParams.toString()}`);
  };

  const columns: TableColumnProps<ArtistAbout>[] = [
    {
      title: '信息 ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'SUPP ID',
      dataIndex: 'userNumber',
      key: 'userNumber',
    },
    {
      title: '真实姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '简介',
      dataIndex: 'introduction',
      key: 'introduction',
      width: 500,
      render: (item: string) => {
        return item
          ? item.length > 100
            ? `${item.slice(0, 100)}......`
            : item
          : '-';
      },
    },
    {
      title: '履历',
      dataIndex: 'resume',
      key: 'resume',
      width: 500,
      render: (item: string) => {
        return item
          ? item.length > 100
            ? `${item.slice(0, 100)}......`
            : item
          : '-';
      },
    },
    {
      title: '外平台个人主页',
      key: 'thirdParty',
      width: 300,
      render: (_, record) => {
        return (
          <Space
            direction="vertical"
            size="small"
            style={{ wordBreak: 'break-all' }}
          >
            {record.facebookUrl && (
              <Link
                to={ensureHttpPrefix(record.facebookUrl)}
                target="_blank"
                rel="noreferrer"
              >
                {record.facebookUrl}
              </Link>
            )}
            {record.instagramUrl && (
              <Link
                to={ensureHttpPrefix(record.instagramUrl)}
                target="_blank"
                rel="noreferrer"
              >
                {record.instagramUrl}
              </Link>
            )}
            {record.twitterUrl && (
              <Link
                to={ensureHttpPrefix(record.twitterUrl)}
                target="_blank"
                rel="noreferrer"
              >
                {record.twitterUrl}
              </Link>
            )}
            {record.websiteUrl && (
              <Link
                to={ensureHttpPrefix(record.websiteUrl)}
                target="_blank"
                rel="noreferrer"
              >
                {record.websiteUrl}
              </Link>
            )}
          </Space>
        );
      },
    },
    {
      title: '审核状态',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        return (
          <Tag
            color={
              record.status === ArtistAboutStatus.APPROVED
                ? 'green'
                : record.status === ArtistAboutStatus.REJECTED
                  ? 'red'
                  : 'default'
            }
          >
            {record.status === ArtistAboutStatus.APPROVED
              ? '已发布'
              : record.status === ArtistAboutStatus.REJECTED
                ? '已驳回'
                : '待编辑'}
          </Tag>
        );
      },
    },
    {
      title: '审核时间',
      dataIndex: 'auditTime',
      key: 'auditTime',
      render: (_, record) => {
        return record.auditTime
          ? dayjs(record.auditTime).format(FORMAT_TIME)
          : '-';
      },
    },
    {
      title: '操作人员',
      dataIndex: 'auditUserName',
      key: 'auditUserName',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => {
        return (
          <Button type="link" onClick={() => handleEditClick(record.id)}>
            编辑
          </Button>
        );
      },
    },
  ];

  const queryArtistAbout = async (
    page: number,
    pageSize: number,
    tabKey?: string
  ) => {
    const values = form.getFieldsValue();
    const params: ArtistAboutListParams = {
      currentPage: page,
      pageSize: pageSize,
      id: values.id ? values.id : undefined,
      submitTime: values.submitTime
        ? dayjs(values.submitTime).format('YYYY-MM-DD')
        : undefined,
      auditUserName: values.auditUserId ? values.auditUserId : undefined,
      suppId: values.suppId ? values.suppId : undefined,
      userName: values.userName ? values.userName : undefined,
      email: values.email ? values.email : undefined,
      status: tabKey !== 'all' ? Number(tabKey) : undefined,
    };
    const resp = await getArtistAboutList(params);
    if (resp.status !== 200) {
      return {
        total: 0,
        currentPage: page,
        pageSize: pageSize,
        records: [],
      };
    }
    return resp.data;
  };

  return (
    <div className="artist-about-container">
      <SearchForm form={form} onFinish={onFinish} items={SearchFormItems} />
      <div style={{ marginTop: 10 }}>
        <TabsTable
          ref={tabsTableRef}
          defaultTabKey="all"
          tabItems={tabItems}
          queryKey="artistAbout"
          queryFn={queryArtistAbout}
          columns={columns}
        />
      </div>
    </div>
  );
};

export default ArtistAboutPage;
